"use client";

import { useState, useEffect, useTransition } from "react";
import { getDownloadCount, incrementDownloadCount } from "@/lib/actions/postStats";

interface DownloadButtonProps {
  slug: string;
  pdfUrl: string;
  fileName: string;
}

export default function DownloadButton({
  slug,
  pdfUrl,
  fileName,
}: DownloadButtonProps) {
  const [downloadCount, setDownloadCount] = useState<number | null>(null);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    const fetchDownloadCount = async () => {
      const result = await getDownloadCount(slug);
      if (result.success) {
        setDownloadCount(result.downloadCount);
      }
    };
    fetchDownloadCount();
  }, [slug]);

  const handleDownload = () => {
    startTransition(async () => {
      // 낙관적 업데이트: 즉시 다운로드 수 증가
      const originalCount = downloadCount;
      const newCount = (downloadCount ?? 0) + 1;
      setDownloadCount(newCount);

      // 즉시 다운로드 시작
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      try {
        const result = await incrementDownloadCount(slug);
        if (result.success) {
          // 서버에서 정확한 카운트 받아서 동기화
          setDownloadCount(result.downloadCount ?? newCount);
        } else {
          // 실패 시 원래 값으로 롤백
          setDownloadCount(originalCount);
          console.error("다운로드 카운트 업데이트 실패:", result.error);
        }
      } catch (error) {
        // 에러 시 원래 값으로 롤백
        setDownloadCount(originalCount);
        console.error("다운로드 카운트 업데이트 에러:", error);
      }
    });
  };

  return (
    <div className="my-4 text-center">
      <button
        onClick={handleDownload}
        disabled={isPending}
        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition duration-300 ease-in-out disabled:bg-gray-400"
      >
        {isPending ? "처리 중..." : "PDF 다운로드"}
      </button>
      <p className="text-sm text-gray-500 mt-2">
        다운로드 횟수:{" "}
        {downloadCount !== null ? downloadCount : "불러오는 중..."}
      </p>
    </div>
  );
}
